import React, { useState, useEffect } from "react";
import { ActionHandlerFunction, AdminApprovalGroup, AdminUser } from "@preveil-api";
import {
  Account, AdminUIActionTypes, AdminErrorMessages, account_types, dayjs, Message, MessageHandlerDisplayType, useAppDispatch, useAppSelector, AdminMessages,
  MessageAnchors, MessageToastTypes, HEADERS, BOM, OrgUserRoles, OrgUserStatus
} from "src/common";
import { Icon, ToolbarWrapper, ToolbarFilter, ToolbarButton } from "src/components";
import { AddUsersModal, BulkConvertToExpressModal, ResendInvitesModal, SetRecoveryGroupModal } from "../toolbar-actions";
import { uiActions } from "src/store";
import { RootState } from "src/store/configureStore";
import saveAs from "file-saver";

type AllProps = {
  current_account: Account;
  is_loading: boolean;
  users_list: AdminUser[];
  handleAction: ActionHandlerFunction;
  filter_param?: string;
  recovery_groups: AdminApprovalGroup[];
}

function UsersToolbarComponent(props: AllProps) {
  const { is_loading, users_list, filter_param, handleAction, recovery_groups, current_account } = props;
  const dispatch = useAppDispatch();
  const selected = useAppSelector((state: RootState) => state.admin.selected);
  const [set_recovery_group_disabled, setSetRecoveryGroupDisabled] = useState<boolean>(false);
  const [send_invites_disabled, setSendInvitesDisabled] = useState<boolean>(false);
  const [show_add_users_modal, setShowAddUsersModal] = useState<boolean>(false);
  const [show_resend_invites_modal, setShowResendInvitesModal] = useState<boolean>(false);
  const [show_set_recovery_group_modal, setShowSetRecoveryGroupModal] = useState<boolean>(false);
  const [show_convert_to_express_modal, setShowConvertToExpressModal] = useState<boolean>(false);
  const [selected_users, setSelectedUsers] = useState<AdminUser[]>([]);
  const [pending_users, setPendingUsers] = useState<AdminUser[]>([]);
  const [express_conversion_invalid_users, setExpressConversionInvalidUsers] = useState<Array<[AdminUser, string]>>([]);
  const [express_conversion_valid_users, setExpressConversionValidUsers] = useState<AdminUser[]>([]);

  useEffect(() => {
    const users = users_list.filter((user) => selected.includes(user.user_id));
    setSelectedUsers(users);
    const pending_users = users.filter((user) => user.status === "pending");
    setPendingUsers(pending_users);
    const contains_pending = pending_users.length === 0;
    setSendInvitesDisabled(contains_pending);
    const all_pending = pending_users.length === users.length;
    setSetRecoveryGroupDisabled(all_pending);
  }, [selected]);

  // Description: Determine which users are valid and invalid for express conversion.
  useEffect(() => {
    const invalid_users: Array<[AdminUser, string]> = [];
    const valid_users: AdminUser[] = [];
    selected_users.forEach((user) => {
      if (user.account_type === account_types.express) {
        invalid_users.push([user, "Express account"]);
      }
      else if (user.role === OrgUserRoles.admin) {
        invalid_users.push([user, "Administrator"]);
      }
      else if (user.status !== OrgUserStatus.active) {
        invalid_users.push([user, "Inactive account"]);
      }
      else if (recovery_groups?.find((recovery_group) => 
        recovery_group.approvers.find((approver) => approver.address === user.user_id)
      )) {
        invalid_users.push([user, "Approval Group"]);
      } else if (user.pending_account_type === account_types.express) {
        invalid_users.push([user, "Already Pending"]);
      }
      else {
        valid_users.push(user);
      }
    });
    setExpressConversionInvalidUsers(invalid_users);
    setExpressConversionValidUsers(valid_users);
  }, [selected_users]);

  // Description: CSV Export for the users list.
  function handleExportUsersList() {
    try {
      const csv_string = [
        [...HEADERS],
        ...users_list.map((user: AdminUser) => [
          user.display_name,
          user.user_id,
          user.account_type === account_types.full ? "Full Account User" : user.account_type === account_types.express ? "Express User" : "Pending",
          user.status,
          user.department,
          user.role,
          !!user.recovery_group ? user.recovery_group.name : "Not Assigned"
        ])
      ]
        .map(row => row.join(","))
        .join("\n");

      const csv_data = `${BOM}${csv_string}`;
      const blob = new Blob([csv_data], { type: "text/csv; charset=utf-8" });
      saveAs(blob, `admin-users-${dayjs(new Date()).format("MM.DD.YYYY-HH_mm_ss")}.csv`);
    } catch (error) {
      dispatch(uiActions.handleRequestErrors(new Message(AdminErrorMessages.error_admin_users_export_csv, MessageHandlerDisplayType.toastr), error));
    }
  };

  // Description: Confirm dialog for deleting selected users.
  function confirmDelete() {
    const selected_user = users_list.find((user) => selected.includes(user.user_id));
    const confirmation_dialog = new Message(
      AdminMessages.confirm_delete_admin_users.body.replace(
        MessageAnchors.message_content,
        selected.length > 1
          ? `these <b>${selected.length}</b> accounts?`
          : !!selected_user ? `user <b>${selected_user.display_name}'s</b> (<b>${selected_user.user_id}</b>)?` : ""
      ),

      MessageHandlerDisplayType.confirm,
      MessageToastTypes.primary,
      AdminMessages.confirm_delete_admin_users.title,
      {
        label: "Yes",
        data: true,
        action: () => handleAction({ actionType: AdminUIActionTypes.Delete })
      },
      {
        label: "No"
      }
    );
    dispatch(uiActions.handleSetMessage(confirmation_dialog));
  }

  // Description: Action Modals for Add Users, Resend Invites and Set Recovery Group.
  function toolbarModals() {
    return <>
      <AddUsersModal
        current_account={current_account}
        org_users={users_list}
        show_modal={show_add_users_modal}
        setShowModal={setShowAddUsersModal}
        handleAction={handleAction}
      />

      <ResendInvitesModal
        current_account={current_account}
        show_modal={show_resend_invites_modal}
        setShowModal={setShowResendInvitesModal}
        handleAction={handleAction}
        users={pending_users}
      />

      <SetRecoveryGroupModal
        current_account={current_account}
        show_modal={show_set_recovery_group_modal}
        setShowModal={setShowSetRecoveryGroupModal}
        recovery_groups={recovery_groups}
        handleAction={handleAction}
        selected_users={selected_users}
        users_list={users_list}
      />

      <BulkConvertToExpressModal 
        current_account={current_account}
        show_modal={show_convert_to_express_modal}
        setShowModal={setShowConvertToExpressModal}
        handleAction={handleAction}
        invalid_users={express_conversion_invalid_users}
        valid_users={express_conversion_valid_users}
      />
    </>;
  }

  return <>
    <ToolbarWrapper
      leftContent={<ToolbarFilter
        disabled={users_list.length === 0}
        filter_param={filter_param ?? ""}
        onChange={(value) => handleAction({ actionType: AdminUIActionTypes.SearchParam, params: value })}
        onClick={() => handleAction({ actionType: AdminUIActionTypes.SearchParam })} />}
      refreshAction={() => handleAction({ actionType: AdminUIActionTypes.GetRefreshRequest })}
      refreshTooltipContent="Refresh Users"
      isLoading={is_loading}
      paginationInfo={users_list.length > 0 ? <span>{`${users_list.length} User${users_list.length > 1 ? "s" : ""}`}</span> : undefined}
    >
      <ToolbarButton
        onClick={() => setShowSetRecoveryGroupModal(true)}
        disabled={set_recovery_group_disabled || selected.length === 0}
        ariaLabel="Set Recovery Group"
        tooltipContent={pending_users.length > 0 ? "Recovery Groups cannot be set on Pending Accounts" : "Set Recovery Group For Selected Accounts"}>
        <Icon className="ficon-users" />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => setShowResendInvitesModal(true)}
        disabled={send_invites_disabled || selected.length === 0}
        ariaLabel="Resend Invites"
        tooltipContent={pending_users.length !== selected.length ? "Invitations can only be sent to Pending Users" : "Resend Invitations"}>
        <Icon className="ficon-mail" />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => setShowConvertToExpressModal(true)}
        disabled={express_conversion_valid_users.length === 0}
        ariaLabel="Convert to Express"
        tooltipContent={express_conversion_valid_users.length === 0 && express_conversion_invalid_users.length > 0 ? "No valid users are selected for conversion" : "Convert to Express"}>
        <Icon className="ficon-user" />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => confirmDelete()}
        disabled={selected.length === 0}
        ariaLabel="Delete Selected"
        tooltipContent="Delete Selected">
        <Icon className="ficon-trash-2" />
      </ToolbarButton>
      <span className="divider" />
      <ToolbarButton
        onClick={() => setShowAddUsersModal(true)}
        ariaLabel="Add Users"
        tooltipContent="Add Users">
        <Icon className="ficon-plus" />
      </ToolbarButton>
      <ToolbarButton
        onClick={() => handleExportUsersList()}
        ariaLabel="Export Users List"
        tooltipContent="Export Users List">
        <Icon className="ficon-download" />
      </ToolbarButton>
    </ToolbarWrapper>
    {toolbarModals()}
  </>;
}

export default React.memo(UsersToolbarComponent);
